/**
 * Reset-password landing page wiring — Phase 3.12.
 *
 * Lives on `/account/reset-password?token=…`. Browsers deliberately drop
 * `<input type="password">` values when the server re-renders the form on
 * a POST round-trip, so a server-side ModelState error for password
 * mismatch means the user has to retype BOTH fields. Cheap fix:
 * client-side check on input — disable the submit button when the two
 * fields differ and surface an inline hint. The server-side
 * `[Compare]` validator remains the source of truth.
 *
 * No-ops when the target elements aren't on the page (the bundle is
 * loaded globally by `_Layout.cshtml` via `amri-login.js`).
 */

const NEW_PWD_ID = 'js-reset-password-new';
const CONFIRM_PWD_ID = 'js-reset-password-confirm';
const HINT_ID = 'js-reset-password-mismatch-hint';
const HINT_TEXT = "Passwords don't match.";

function findSubmitButton(newPwd: HTMLInputElement): HTMLButtonElement | null {
    const form = newPwd.closest('form');
    if (!form) {
        return null;
    }

    return form.querySelector<HTMLButtonElement>('button[type="submit"]');
}

function ensureHint(newPwd: HTMLInputElement): HTMLElement {
    const existing = document.getElementById(HINT_ID);
    if (existing) {
        return existing;
    }

    const hint = document.createElement('div');
    hint.id = HINT_ID;
    hint.className = 'amri-form-error';
    hint.setAttribute('role', 'alert');
    hint.textContent = HINT_TEXT;
    hint.style.display = 'none';

    // Insert immediately after the confirm field's wrapper so the message
    // sits below the second password input.
    const confirm = document.getElementById(CONFIRM_PWD_ID) as HTMLInputElement | null;
    const anchor = confirm?.closest('.form-group') ?? newPwd.closest('.form-group');
    if (anchor && anchor.parentElement) {
        anchor.parentElement.insertBefore(hint, anchor.nextSibling);
    } else {
        newPwd.parentElement?.appendChild(hint);
    }

    return hint;
}

function syncMismatchState(newPwd: HTMLInputElement, confirmPwd: HTMLInputElement, hint: HTMLElement, submit: HTMLButtonElement | null): void {
    const both = newPwd.value.length > 0 && confirmPwd.value.length > 0;
    const mismatch = both && newPwd.value !== confirmPwd.value;

    hint.style.display = mismatch ? '' : 'none';
    if (submit) {
        submit.disabled = mismatch;
    }
}

export function initResetPasswordForm(): void {
    const newPwd = document.getElementById(NEW_PWD_ID) as HTMLInputElement | null;
    const confirmPwd = document.getElementById(CONFIRM_PWD_ID) as HTMLInputElement | null;
    if (!newPwd || !confirmPwd) {
        return;
    }

    const hint = ensureHint(newPwd);
    const submit = findSubmitButton(newPwd);

    const sync = (): void => syncMismatchState(newPwd, confirmPwd, hint, submit);
    newPwd.addEventListener('input', sync);
    confirmPwd.addEventListener('input', sync);
    sync();
}
